import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import { DataGrid, GridColumns } from "@mui/x-data-grid";
import {
  DateField,
  EditButton,
  EmailField,
  List,
  useDataGrid,
} from "@refinedev/mui";
import MutedStatus from "@warriortrading/warriortrading.hermes.common.feathers/enums/mutedStatus";
import UserGroup from "@warriortrading/warriortrading.hermes.common.feathers/enums/userGroup";
import UserStatus from "@warriortrading/warriortrading.hermes.common.feathers/enums/userStatus";
import { DeleteButton } from "components/buttons/delete";
import { ImpersonateButton } from "components/buttons/impersonate";
import { RecycleButton } from "components/buttons/recycle";
import { RemoveButton } from "components/buttons/remove";
import React, { useState } from "react";
import { groupIsAdmin } from "rest-data-provider/utils/tools";

export const UserList = () => {
  const { dataGridProps, setFilters, setCurrent, setPageSize } = useDataGrid({
    sorters: {
      initial: [
        {
          field: "updatedAt",
          order: "desc",
        },
      ],
    },
    pagination: {
      pageSize: 10,
    },
  });

  const [id, setId] = useState("");
  const [uuid, setUuid] = useState("");
  const [email, setEmail] = useState("");
  const [mutedStatus, setMutedStatus] = useState("");
  const [prodCategory, setProdCategory] = useState("");
  const [quotestreamPackage, setQuotestreamPackage] = useState("");
  const [userGroup, setUserGroup] = useState("");
  const [statusTypeSearch, setStatusTypeSearch] = useState<number>(2);
  const mutedStatusArr = Object.values(MutedStatus)[0].map((item: any) => {
    return { value: item.value, label: item.name };
  });
  const userGroupArr = Object.values(UserGroup)[0].map((item: any) => {
    return { value: item.value, label: item.name };
  });

  const avatarDefaultUrl = "avatar_default_chat.svg";
  let columns = React.useMemo<GridColumns<any>>(
    () => [
      {
        field: "id",
        headerName: "Id",
        type: "number",
        minWidth: 50,
        hideable: false,
      },
      {
        field: "nickname",
        flex: 1,
        headerName: "Nickname",
        minWidth: 120,
        hideable: false,
      },
      {
        field: "email",
        flex: 1,
        headerName: "Email",
        minWidth: 200,
        renderCell: function render({ value }) {
          return <EmailField value={value} />;
        },
        hideable: false,
      },
      {
        field: "avatarUrl",
        flex: 1,
        headerName: "Avatar Url",
        minWidth: 100,
        renderCell: function render({ value }) {
          return (
            <img
              src={
                value !== "" && value !== "/avatar_default_chat.svg"
                  ? value
                  : avatarDefaultUrl
              }
              alt="avatarUrl"
              style={{ height: "50px", maxWidth: "100px" }}
            />
          );
        },
        hideable: false,
      },
      {
        field: "prodCategories",
        flex: 1,
        headerName: "Product Categories",
        type: "number",
        minWidth: 120,
        hideable: false,
      },
      {
        field: "quotestreamPackages",
        flex: 1,
        headerName: "Packages",
        type: "number",
        minWidth: 120,
        hideable: false,
      },
      {
        field: "status",
        flex: 1,
        headerName: "Status",
        type: "number",
        minWidth: 80,
        renderCell: function render({ row }) {
          return UserStatus.fromValue(row.status)?.name;
        },
        hideable: false,
      },
      {
        field: "mutedStatus",
        flex: 1,
        headerName: "Muted Status",
        type: "number",
        minWidth: 100,
        renderCell: function render({ value }) {
          return <span>{MutedStatus.fromValue(value).name}</span>;
        },
        hideable: false,
      },
      {
        field: "group",
        flex: 1,
        headerName: "Group",
        type: "number",
        minWidth: 80,
        renderCell: function render({ value }) {
          return <span>{UserGroup.fromValue(value).name}</span>;
        },
        hideable: false,
      },
      {
        field: "signedUpDate",
        flex: 1,
        headerName: "Signed Up Date",
        minWidth: 120,
        renderCell: function render({ value }) {
          return value ? <DateField value={value} /> : null;
        },
        hideable: false,
      },
      {
        field: "updatedAt",
        flex: 1,
        headerName: "Updated At",
        minWidth: 100,
        renderCell: function render({ value }) {
          return <DateField value={value} />;
        },
        hideable: false,
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        renderCell: function render({ row }) {
          return (
            <>
              {row.status ? null : (
                <EditButton hideText recordItemId={row.id} />
              )}
              {row.status ? (
                <RecycleButton hideText recordItemId={row.id} />
              ) : (
                <RemoveButton hideText recordItemId={row.id} />
              )}
              <DeleteButton hideText recordItemId={row.id} />
              {row.status ? null : (
                <ImpersonateButton hideText record={row} />
              )}
            </>
          );
        },
        align: "center",
        headerAlign: "center",
        minWidth: 160,
        hideable: false,
      },
    ],
    []
  );

  // if (groupIsAdmin()) {
  //   columns = columns.filter((e) => e.field !== "actions");
  // }

  const handleFilter = () => {
    setCurrent(1);
    setPageSize(10);
    let searchArr: { field: string; value: string; operator: string }[] = [];
    if (id) {
      searchArr.push({
        field: "id",
        value: `${id}`,
        operator: "eq",
      });
    }
    if (uuid) {
      searchArr.push({
        field: "uuid",
        value: `${uuid}`,
        operator: "eq",
      });
    }
    if (email) {
      searchArr.push({
        field: "email[$ilike]",
        value: `%${email}%`,
        operator: "eq",
      });
    }
    if (prodCategory) {
      searchArr.push({
        field: "prodCategories[$ilike]",
        value: `%${prodCategory}%`,
        operator: "eq",
      });
    }
    if (quotestreamPackage) {
      searchArr.push({
        field: "quotestreamPackages[$ilike]",
        value: `%${quotestreamPackage}%`,
        operator: "eq",
      });
    }
    if (mutedStatus !== "") {
      searchArr.push({
        field: "mutedStatus",
        value: `${mutedStatus}`,
        operator: "eq",
      });
    }
    if (userGroup !== "") {
      searchArr.push({
        field: "group",
        value: `${userGroup}`,
        operator: "eq",
      });
    }
    if (statusTypeSearch === 1 || statusTypeSearch === 0) {
      searchArr.push({
        field: "status",
        value: `${statusTypeSearch}`,
        operator: "eq",
      });
    }
    setFilters(searchArr as any);
  };

  const clearFun = () => {
    setId("");
    setUuid("");
    setProdCategory("");
    setQuotestreamPackage("");
    setCurrent(1);
    setPageSize(10);
    setEmail("");
    setMutedStatus("");
    setUserGroup("");
    setStatusTypeSearch(2);
    setFilters([]);
  };
  return (
    <List>
      <FormControlLabel
        label=""
        style={{ width: "100%" }}
        control={
          <div style={{ width: "100%" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                width: "100%",
                marginLeft: "19px",
                paddingRight: "15px",
                marginBottom: "12px",
              }}
            >
              <TextField
                id="standard-basic"
                label="Id"
                variant="standard"
                margin="normal"
                value={id}
                onChange={(e) => setId(e.target.value)}
                style={{ width: "30%" }}
              />
              <TextField
                id="standard-basic"
                label="Uuid"
                variant="standard"
                margin="normal"
                value={uuid}
                onChange={(e) => setUuid(e.target.value)}
                style={{ width: "30%" }}
              />
              <TextField
                id="standard-basic"
                label="Email"
                variant="standard"
                margin="normal"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ width: "30%" }}
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                width: "100%",
                marginLeft: "19px",
                paddingRight: "15px",
                marginBottom: "12px",
              }}
            >
              <TextField
                select
                value={mutedStatus}
                defaultValue={0}
                label="Muted Status"
                variant="standard"
                margin="normal"
                style={{ width: "30%" }}
                onChange={(e) => {
                  setMutedStatus(e.target.value as unknown as string);
                }}
              >
                {mutedStatusArr.map((option: any) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                value={userGroup}
                defaultValue={0}
                margin="normal"
                label="Group"
                variant="standard"
                style={{ width: "30%" }}
                onChange={(e) => {
                  setUserGroup(e.target.value as unknown as string);
                }}
              >
                {userGroupArr.map((option: any) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <FormControl style={{ width: "30%", marginBottom: "8px" }}>
                <InputLabel id="status">Status: </InputLabel>
                <Select
                  labelId="status"
                  value={statusTypeSearch as any}
                  label="Status"
                  variant="standard"
                  onChange={(event: SelectChangeEvent) => {
                    setStatusTypeSearch(event.target.value as any);
                  }}
                >
                  {Object.values(UserStatus)[0].map((item: any) => {
                    return (
                      <MenuItem key={item.value} value={item.value}>
                        {item.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                width: "100%",
                marginLeft: "19px",
                paddingRight: "15px",
                marginBottom: "12px",
              }}
            >
              <TextField
                id="standard-basic"
                label="Product Category Id"
                variant="standard"
                margin="normal"
                value={prodCategory}
                onChange={(e) => setProdCategory(e.target.value)}
                style={{ width: "30%" }}
              />
              <TextField
                id="standard-basic"
                label="Quotestream Package Id"
                variant="standard"
                margin="normal"
                value={quotestreamPackage}
                onChange={(e) => setQuotestreamPackage(e.target.value)}
                style={{ width: "30%" }}
              />
              <div style={{ width: "30%" }}>
                <ClearOutlinedIcon onClick={clearFun} />
                <SearchIcon onClick={handleFilter} />
              </div>
            </div>
          </div>
        }
      />
      <DataGrid
        {...dataGridProps}
        columns={columns}
        autoHeight
        paginationMode="server"
        rowsPerPageOptions={[10, 20, 50]}
        disableColumnFilter
      />
    </List>
  );
};
